// src/components/layout/topbar.tsx
import { useNavigate } from '@tanstack/react-router';
import { Bell, ChevronDown, KeyRound, LogOut, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Breadcrumbs } from './breadcrumbs';
import { CommandPalette } from './command-palette';
import { useCommandPalette } from './use-command-palette';
import { useAuthStore } from '@/auth/auth-store';
import { useLogout } from '@/auth/hooks';
import { useTeamAlerts } from '@/features/team-alerts/hooks';
import { useMyStores } from '@/features/tienda/hooks';
import { StoreSwitcher } from '@/features/tienda/store-switcher';
import { useActiveStore } from '@/features/tienda/use-active-store';

/** The role as the operator would say it, not as the JWT spells it. */
const ROLE_LABEL: Record<string, string> = {
  ADMIN: 'Administrador',
  STAFF: 'Personal',
  // 2026-11-02 — a store owner signs in with their platform account (role USER).
  USER: 'Tienda',
};

/**
 * The top bar had three controls that did nothing: a search box with no
 * handler, a bell with no inbox and a user button with no menu. A control
 * that does nothing teaches the operator to stop trying controls. Each one
 * now does the thing it looks like it does:
 *
 *   - the search box opens the palette (`CommandPalette`, also on ⌘K);
 *   - the bell, for an ADMIN, counts the team alerts nobody has read and
 *     opens them (ko-admin#344: a STAFF member is not among the recipients,
 *     so they get no bell rather than a bell that is always empty);
 *   - the user button opens a menu with who is signed in, their second factor
 *     and the way out.
 */
export function Topbar() {
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useCommandPalette();
  const isAdmin = user?.role === 'ADMIN';
  const isStoreOwner = user?.role === 'USER';
  const alerts = useTeamAlerts(isAdmin);
  const unread = alerts.data?.items.filter((a) => a.readAt === null).length ?? 0;
  // The store owner's bar names their store where the staff see the bell; with
  // several stores it becomes the switcher.
  const { stores } = useMyStores(isStoreOwner);
  const [activeStore, setActiveStore] = useActiveStore(stores);

  const name = user?.name ?? user?.email ?? 'Admin';
  const role = user?.role ?? 'ADMIN';
  const initial = name.charAt(0).toUpperCase();

  return (
    <header className="flex h-14 shrink-0 items-center justify-between gap-4 border-b border-border bg-bg px-6">
      <div className="flex min-w-0 items-center gap-6">
        <Breadcrumbs />
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          className="relative hidden h-10 w-72 items-center gap-2 rounded-lg border border-border bg-surface pl-3 pr-14 text-left text-sm text-text-muted outline-none transition-colors hover:border-gold/50 hover:text-text-secondary focus-visible:ring-1 focus-visible:ring-gold md:flex"
        >
          <Search size={16} aria-hidden />
          <span className="truncate">Ir a una sección o persona…</span>
          <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border border-border bg-graphite px-1.5 py-0.5 text-2xs font-medium text-text-muted">
            ⌘K
          </kbd>
        </button>
        <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />

        {isStoreOwner && (
          <StoreSwitcher stores={stores} active={activeStore} onChange={setActiveStore} />
        )}

        {isAdmin && (
          <button
            type="button"
            aria-label={
              unread > 0
                ? `Avisos al equipo: ${unread} sin leer`
                : 'Avisos al equipo: nada sin leer'
            }
            title="Avisos al equipo"
            onClick={() => void navigate({ to: '/', hash: 'avisos' })}
            className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface text-text-secondary outline-none transition-colors hover:text-text-primary focus-visible:ring-1 focus-visible:ring-gold"
          >
            <Bell size={18} aria-hidden />
            {unread > 0 && (
              <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red px-1 text-2xs font-semibold leading-none text-white">
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Menú de usuario"
              className="flex items-center gap-2 rounded-lg border border-border bg-surface py-1.5 pl-1.5 pr-2 outline-none transition-colors hover:bg-elevated focus-visible:ring-1 focus-visible:ring-gold"
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gold text-xs font-bold text-bg">
                {initial}
              </span>
              <span className="hidden text-left leading-tight sm:block">
                <span className="block text-sm text-text-primary">{name}</span>
                <span className="block text-2xs text-text-muted">{ROLE_LABEL[role] ?? role}</span>
              </span>
              <ChevronDown size={16} className="text-text-muted" aria-hidden />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="min-w-[14rem]">
            <div className="px-2 py-1.5 leading-tight">
              <div className="text-sm text-text-primary">{name}</div>
              {user?.email && user.email !== name && (
                <div className="truncate text-xs text-text-muted">{user.email}</div>
              )}
              <div className="mt-0.5 text-2xs text-text-muted">{ROLE_LABEL[role] ?? role}</div>
            </div>
            <div className="my-1 border-t border-border" />
            <DropdownMenuItem
              onSelect={() => void navigate({ to: '/security' })}
              hint="Tu app de autenticación: cámbiala si cambiaste de teléfono."
            >
              <span className="flex items-center gap-2">
                <KeyRound size={14} aria-hidden /> Mi segundo factor
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => void logout()} danger>
              <span className="flex items-center gap-2">
                <LogOut size={14} aria-hidden /> Cerrar sesión
              </span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button variant="ghost" size="sm" onClick={() => void logout()} aria-label="Cerrar sesión">
          <LogOut size={18} aria-hidden />
        </Button>
      </div>
    </header>
  );
}
